import axios from "axios";
import { sessionUtils } from "@/lib/session-utils";
import { config } from "../config/config";

// Type definitions for API responses
interface LoginResponse {
  id: string;
  userId: any;
  accessToken: string | undefined;
  refreshToken: string | undefined;
  name: any;
  username: any;
  user_name: any;
  avatar: any;
  profile_picture: any;
  message: string;
  user_id: string;
  token?: string;
  access_token?: string;
  refresh_token?: string;
  api_key?: string;
  roles?: string[];
  modules?: string[];
  permissions?: string[];
  tenant_id?: string;
  is_demo?: boolean;

  //license info
  licenses_info?: {
    scan_limit?: number;
    location?: string;
    issued_at?: string;
    valid_until?: string;
    is_active?: boolean;
  } | null;

  // tenant status (present when tenant is inactive, null or absent when active)
  tenant_status?: {
    is_inactive: boolean;
    inactivated_at: string;
    days_inactive: number;
    history_access_remaining_days: number;
    scan_allowed: boolean;
    history_allowed: boolean;
    message: string;
  } | null;
}

// const BASE_URL = "http://agmd.bitxiatech.com:8011";
// const BASE_URL_VS = "http://agmd.bitxiatech.com:8010";
const BASE_URL = config.API_URL + "/api";
const BASE_URL_VS = config.API_URL + "/dast";
const BASE_URL_AI = (config as any).AI_API_URL || BASE_URL_VS;

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
  },
});

// Session management - using sessionUtils for consistency
export const sessionManager = {
  setUserId: sessionUtils.setUserId,
  getUserId: sessionUtils.getUserId,
  setToken: sessionUtils.setToken,
  getToken: sessionUtils.getToken,
  setRefreshToken: sessionUtils.setRefreshToken,
  getRefreshToken: sessionUtils.getRefreshToken,
  setApiKey: sessionUtils.setApiKey,
  getApiKey: sessionUtils.getApiKey,
  isAuthenticated: sessionUtils.isAuthenticated,
  clearSession: sessionUtils.clearSession,
  setRoles: sessionUtils.setRoles,
  getRoles: sessionUtils.getRoles,
  setModules: sessionUtils.setModules,
  getModules: sessionUtils.getModules,
  setPermissions: sessionUtils.setPermissions,
  getPermissions: sessionUtils.getPermissions,
  setTenantId: sessionUtils.setTenantId,
  getTenantId: sessionUtils.getTenantId,
  setTenantStatus: sessionUtils.setTenantStatus,
  getTenantStatus: sessionUtils.getTenantStatus,
  setIsDemo: sessionUtils.setIsDemo,
  getIsDemo: sessionUtils.getIsDemo,
  getCurrentUser: () => {
    const userId = sessionManager.getUserId();
    const token = sessionManager.getToken();
    const refreshToken = sessionManager.getRefreshToken();
    const apiKey = sessionManager.getApiKey();
    return {
      userId,
      token,
      refreshToken,
      apiKey,
      isAuthenticated: !!userId,
    };
  },
};

// Request interceptor to add user ID and token to all requests
api.interceptors.request.use(
  (config) => {
    const userId = sessionManager.getUserId();
    const token = sessionManager.getToken();

    if (userId) {
      config.headers["X-User-ID"] = userId;
    }

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Clear session on unauthorized access


       sessionUtils.clearSession();
      // Optionally redirect to login page
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export const API_ENDPOINTS = {
  CREATE_TENANT: "/tenant/create",
  CREATE_USER: "/user/create",
  GET_ALL_USERS: "/user/all",
  LOGIN_TENANT: "/tenant/login",
  RESET_PASSWORD: "/user/resetPassword",
  SAST_FILE_SCAN: "/code-guard/sast/file/scan/pdf",
  GIT_REPO_SCAN: "/code-guard/git/pdf/scan",
  SCAN_HISTORY: "/code-guard/scan-history",
  SCA_FILE_SCAN: "/code-guard/sca/scan/pdf",
  SAST_FOLDER_SCAN: "/code-guard/folder/scan/pdf",
  ALERTS: "/home/alerts",
  THREATS_COUNT: "/home/threats-count/all",
  SEARCH: "/home/alerts",
  START_VS_SCAN: "/start/scan",
  GET_SCAN_ENGINES: "/start/scan-engines",
  GET_VS_SCAN_HISTORY: "/api/v1/scan-results/scan-history",
  GET_VULNERABILITIES: "/api/v1/scan-results/vulnerabilities",
  GET_OS_DETECTIONS: "/api/v1/scan-results/os-detections",
  GET_TECHNOLOGIES: "/api/v1/scan-results/technologies",
  GET_USER_ROLES: "/user/roles",
  GET_USER_FEATURE_ACCESS: "/user/featureAccess",
  GET_VS_SCAN_PORTS: "/api/v1/scan-results/ports-technologies",
  SCAN_PASSWORD: "/scan_passwords",
  GET_MODULES_LIST: "/roles-permissions/modules",
  GET_USER_TYPES: "/user/userTypes",
  UPDATE_USER: "/user/update",
  LEAKGUARD_SCAN_HISTORY: "/leak-guard/scan-history",
  GET_CG_SCAN_HISTORY_RESULT: "/code-guard/scan-history/details",
  TEXT_HIBP: "/scan_passwords_text_basic",
  TEXT_DEHASED: "/scan_passwords_text_custom",
  ADD_NEW_MONITOR: "/monitoring/add-monitoring",
  UPDATE_MONITOR: "/monitoring/update-monitoring",
  GET_ALL_MONITORS: "/monitoring/get-monitoring",
  UPDATE_MONITOR_STATUS: "/monitoring/update-status",
  DELETE_MONITOR: "/monitoring/delete-monitoring",
  UPDATE_USER_STATUS:"/user/update/status",
  ADD_CLOUD_CONFIGURATION: "/api/v1/cspm-scan/add_configuration",
  GET_ALL_CLOUD_PROVIDERS: "/api/v1/cspm-scan/get_all_configurations_for_current_user",
  SCAN_SINGLE_CLOUD_PROVIDER:"/api/v1/cspm-scan/scan_by_id",
  DELETE_CLOUD_PROVIDER:"/api/v1/cspm-scan/remove_configuration",
  GET_ALL_FINDINGS: "/api/v1/cspm-scan/scans",
  GET_ALL_COMPLIANCES: "/api/v1/cspm-scan/compliance/summaries",
  GET_COMPLIANCE_DETAILS: "/api/v1/cspm-scan/compliance",
  GET_COMPLIANCE_FRAMEWORKS: "/api/v1/cspm-scan/compliance-frameworks",
  SEND_OTP: "/otp/send",
  FORGOT_PASSWORD: "/tenant/forgot-password",
  GET_ALL_DSPM_CONFIGURATIONS: "/dspm/config/get_configurations",
  REMOVE_DSPM_DATA_STORE: "/dspm/remove_configuration",
  SCAN_DSPM_DATA_STORE: "/dspm/scan",
  GET_DSPM_SCAN_HISTORY: "/dspm/scan_history",
  GET_ALL_DSPM_FINDINGS: "/dspm/findings",
  GET_ALL_DSPM_RESOURCES: "/dspm/resource",
  GET_SINGLE_DSPM_RESOURCE: "/dspm/detailed_resource",
  GET_ALL_DSPM_COMPLIANCES: "/dspm/compliance",
  GET_SINGLE_DSPM_COMPLIANCE_DETAILS: "/dspm/detailed_compliance",
  ADD_DSPM: "/dspm/config/add_configuration",
  GET_DSPM_OVERVIEW_DATA: "/dspm/overview",
  VS_SCAN_NEW: "/start/scan/batch",
  VS_AUTH_SCAN: "/start/scan/Auth-batch",
  VS_UNAUTH_SCAN: "/start/scan/unAuth-batch",
  VS_COMBINED_UNAUTH_SCAN: "/start/scan/combined-batch/unauth",
  VS_COMBINED_AUTH_SCAN: "/start/scan/combined-batch/auth",
  VS_NETWORK_BATCH_SCAN: "/start/scan/network-batch",
  VS_COPILOT_CHAT: "/api/v1/copilot/chat",
  VS_COPILOT_CHAT_STREAM: "/api/v1/copilot/chat/stream",
  VS_COPILOT_CHAT_ALIAS: "/api/v1/copilot/copilotchat",
  VS_COPILOT_SCAN_CONVERSATION: "/api/v1/copilot/conversations",
  VS_ATTACK_PATHS_GENERATE: "/api/v1/copilot/attack-paths/generate",
  VS_ATTACK_PATHS_LATEST: "/api/v1/copilot/attack-paths",
  VS_REPORTS_GENERATE: "/api/v1/copilot/reports/generate",
  VS_REPORTS_LATEST: "/api/v1/copilot/reports",
  VS_REPORTS_JOB_STATUS: "/api/v1/copilot/reports/jobs",
  INTERCEPTOR_START: "/interceptor/start",
  INTERCEPTOR_STOP: "/interceptor/stop",
  INTERCEPTOR_ANALYSE: "/interceptor/analyse",
  AI_INTERCEPTOR_START: "/ai/interceptor/start",
  AI_INTERCEPTOR_STOP: "/ai/interceptor/stop",
  AI_INTERCEPTOR_ANALYSE: "/ai/interceptor/analyse",
  GET_AUDIT_LOGS: "/audit/audit-logs",
};

// API service functions
export const tenantService = {
  createTenant: async (tenantData: any) => {
    try {
      const response = await api.post(API_ENDPOINTS.CREATE_TENANT, tenantData);
      return response.data;
    } catch (error) {
      console.error("Error creating tenant:", error);
      throw error;
    }
  },
  GetScanEngines: async () => {
    try {
      const response = await api.get(
        `${BASE_URL_VS}${API_ENDPOINTS.GET_SCAN_ENGINES}`
      );

      //       const response ={
      //     "scan_types": {
      //         "Network": [
      //             "nmap",
      //             "nuclei",
      //             "aloo",
      //             "mirch"
      //         ],
      //         "Web": [
      //             "nuclei"
      //         ],
      //         "Full": [
      //             "nuclei",
      //             "nmap"
      //         ]
      //     }
      // }
      return response.data;
    } catch (error) {
      console.error("Error fetching scan engines:", error);
      throw error;
    }
  },
  getScanResultDetails: async (scanId: string) => {
    try {
      const response = await api.get(
        `${BASE_URL}${API_ENDPOINTS.GET_CG_SCAN_HISTORY_RESULT}/${scanId}`
      );
      return response?.data;
    } catch (error) {
      console.error("Error fetching scan result details:", error);
      throw error;
    }},
    
  GetVSScanHistory: async (page: number = 1, pageSize: number = 10, search?: string) => {
    try {
      const response = await api.get(
        `${BASE_URL_VS}${API_ENDPOINTS.GET_VS_SCAN_HISTORY}`,
        {
          params: {
            page: page,
            page_size: pageSize,
            search,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching scan engines:", error);
      throw error;
    }
  },
GetVulnerabilites: async (
  page: number = 1,
  pageSize: number = 10,
  ScanId: string,
  target?: string // Add optional target parameter
) => {
  try {
    const params: any = {
      page: page,
      page_size: pageSize,
    };

    // Add target parameter if provided
    if (target) {
      params.target = target;
    }

    const response = await api.get(
      `${BASE_URL_VS}${API_ENDPOINTS.GET_VULNERABILITIES}/${ScanId}`,
      {
        params,
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching vulnerabilities:", error);
    throw error;
  }
},

  GetOSDetections: async (
      ScanId: string,
      page: number = 1,
      pageSize: number = 10
    ) => {
      try {
        const response = await api.get(
          `${BASE_URL_VS}${API_ENDPOINTS.GET_OS_DETECTIONS}/${ScanId}`,
          {
            params: {
              page,
              page_size: pageSize,
            },
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching OS detections:", error);
        throw error;
      }
    },

    GetTechnologies: async (
      ScanId: string,
      page: number = 1,
      pageSize: number = 10
    ) => {
      try {
        const response = await api.get(
          `${BASE_URL_VS}${API_ENDPOINTS.GET_TECHNOLOGIES}/${ScanId}`,
          {
            params: {
              page,
              page_size: pageSize,
            },
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching technologies:", error);
        throw error;
      }
    },

  GetScanPorts: async (ScanId: string) => {
    try {
      const response = await api.get(
        `${BASE_URL_VS}${API_ENDPOINTS.GET_VS_SCAN_PORTS}/${ScanId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching scan Ports", error);
      throw error;
    }
  },

  StartInterceptor: async (payload: any) => {
    try {
      const response = await api.post(
        `${BASE_URL_VS}${API_ENDPOINTS.INTERCEPTOR_START}`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error starting interceptor session:", error);
      throw error;
    }
  },

  StopInterceptor: async (sessionId: string) => {
    try {
      const response = await api.post(
        `${BASE_URL_VS}${API_ENDPOINTS.INTERCEPTOR_STOP}/${sessionId}`,
        null,
        { headers: { accept: "application/json" } }
      );
      return response.data;
    } catch (error) {
      console.error("Error stopping interceptor session:", error);
      throw error;
    }
  },

  AnalyseInterceptor: async (sessionId: string) => {
    try {
      const response = await api.post(
        `${BASE_URL_VS}${API_ENDPOINTS.INTERCEPTOR_ANALYSE}/${sessionId}`,
        null,
        { headers: { accept: "application/json" } }
      );
      return response.data;
    } catch (error) {
      console.error("Error analysing interceptor session:", error);
      throw error;
    }
  },

  StartAiInterceptor: async (payload: any) => {
    try {
      const response = await api.post(
        `${BASE_URL_VS}${API_ENDPOINTS.AI_INTERCEPTOR_START}`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error starting AI interceptor session:", error);
      throw error;
    }
  },

  StopAiInterceptor: async (sessionId: string) => {
    try {
      const response = await api.post(
        `${BASE_URL_VS}${API_ENDPOINTS.AI_INTERCEPTOR_STOP}/${sessionId}`,
        null,
        { headers: { accept: "application/json" } }
      );
      return response.data;
    } catch (error) {
      console.error("Error stopping AI interceptor session:", error);
      throw error;
    }
  },

  AnalyseAiInterceptor: async (sessionId: string) => {
    try {
      const response = await api.post(
        `${BASE_URL_VS}${API_ENDPOINTS.AI_INTERCEPTOR_ANALYSE}/${sessionId}`,
        null,
        { headers: { accept: "application/json" } }
      );
      return response.data;
    } catch (error) {
      console.error("Error analysing AI interceptor session:", error);
      throw error;
    }
  },

  loginTenant: async (loginData: { email: string; password: string }) => {
    try {
      const response = await api.post(API_ENDPOINTS.LOGIN_TENANT, loginData);
      const data: LoginResponse = response.data?.data;

      // Store user session data
      if (data.user_id) {
        sessionManager.setUserId(data.user_id);
      }

      // Store tokens (handle both token and access_token fields)
      if (data.token) {
        sessionManager.setToken(data.token);
      } else if (data.access_token) {
        sessionManager.setToken(data.access_token);
      }

      if (data.refresh_token) {
        sessionManager.setRefreshToken(data.refresh_token);
      }

      if(data?.tenant_status !== null && data?.tenant_status !== undefined){
        sessionManager.setTenantStatus(data.tenant_status);
      }

      sessionManager.setIsDemo(data.is_demo ?? false);

      if (data.api_key) {
        sessionManager.setApiKey(data.api_key);
        notifyApiKeyUpdate();
      } else {
        console.log("No api_key found in login response");
      }

      return data;
    } catch (error) {
      console.error("Error logging in tenant:", error);
      throw error;
    }
  },
  logout: async () => {
    try {
      // Clear session data
      sessionManager.clearSession();
      return { success: true };
    } catch (error) {
      console.error("Error during logout:", error);
      throw error;
    }
  },
  resetPassword: async (passwordData: {
    currentPassword: string;
    newPassword: string;
  }) => {
    try {
      const accessToken = sessionUtils.getToken();
      if (!accessToken) {
        throw new Error("Access token not found. Please login again.");
      }

      const response = await api.post(API_ENDPOINTS.RESET_PASSWORD, {
        old_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      }, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error resetting password:", error);
      throw error;
    }
  },
  scanFile: async (
    file: File,
    language: string = "",
    signal?: AbortSignal,
    scanners?: string[]
  ) => {

    
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("language", language);

      // Add scanner parameters
    // scanners = [{ id, name, enabled }, ...]

      if (Array.isArray(scanners)) {
        const enabledScanners = scanners
          .filter(scanner => scanner.enabled)
          .map(scanner => scanner.id);

        // Append only if at least one scanner is enabled
        if (enabledScanners.length > 0) {
          enabledScanners.forEach(id => {
            formData.append("scanners", id);
          });
        }
      }


      const response = await api.post(API_ENDPOINTS.SAST_FILE_SCAN, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        signal,
      });
      return response.data;
    } catch (error) {
      console.error("Error scanning file:", error);
      throw error;
    }
  },
  scanGitRepo: async (
    repoUrl: string,
    branch: string = "main",
    signal?: AbortSignal,
    token?: string,
    scanners?: string[]
  ) => {
    try {
      const formData = new FormData();
      formData.append("repo_url", repoUrl);
      formData.append("branch", branch);

      // Add token if provided
      if (token && token.trim()) {
        formData.append("token", token);
      }

      // Add scanner parameters
      if (Array.isArray(scanners)) {
        const enabledScanners = scanners
          .filter(scanner => scanner.enabled)
          .map(scanner => scanner.id);

        // Append only if at least one scanner is enabled
        if (enabledScanners.length > 0) {
          enabledScanners.forEach(id => {
            formData.append("scanners", id);
          });
        }
      }

      const response = await api.post(API_ENDPOINTS.GIT_REPO_SCAN, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        signal,
      });
      return response.data;
    } catch (error) {
      console.error("Error scanning Git repo:", error);
      throw error;
    }
  },
  getScanHistory: async (page: number = 1, page_size: number = 10, search?: string) => {
    try {
      const response = await api.get(API_ENDPOINTS.SCAN_HISTORY, {
        params: { page, page_size, search },
        headers: { Accept: "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching scan history:", error);
      throw error;
    }
  },
  scanSCAFile: async (
    file: File,
    signal?: AbortSignal,
    scanners?: string[]
  ) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      // Add scanner parameters
       if (Array.isArray(scanners)) {
        const enabledScanners = scanners
          .filter(scanner => scanner.enabled)
          .map(scanner => scanner.id);

        // Append only if at least one scanner is enabled
        if (enabledScanners.length > 0) {
          enabledScanners.forEach(id => {
            formData.append("scanners", id);
          });
        }
      }

      const response = await api.post(API_ENDPOINTS.SCA_FILE_SCAN, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        signal,
      });
      return response.data;
    } catch (error: any) {
      if (error?.response?.data) {
        throw error.response.data;
      }
      console.error("Error scanning SCA file:", error);
      throw error;
    }
  },
  scanSASTFolder: async (
    folderZip: File,
    signal?: AbortSignal,
    scanners?: string[]
  ) => {
    try {
      const formData = new FormData();
      formData.append("folder_zip", folderZip);

      // Add scanner parameters
       if (Array.isArray(scanners)) {
        const enabledScanners = scanners
          .filter(scanner => scanner.enabled)
          .map(scanner => scanner.id);

        // Append only if at least one scanner is enabled
        if (enabledScanners.length > 0) {
          enabledScanners.forEach(id => {
            formData.append("scanners", id);
          });
        }
      }

      const response = await api.post(
        API_ENDPOINTS.SAST_FOLDER_SCAN,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          signal,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error scanning SAST folder:", error);
      throw error;
    }
  },
  getAlerts: async (
    page: number = 1,
    pageSize: number = 10,
    severity?: string,
    from_date?: string,
    to_date?: string,
    source?: string,
    search?: string
  ) => {
    try {
      const params: any = { page, page_size: pageSize, include_charts: true };

      // Add severity filter if provided and not "all"
      if (severity && severity !== "all") {
        params.severity = severity.toLowerCase();
      }
      if(source && source !== "all"){
        params.source=source;
      }

      // Add date range filters if provided
      if (from_date) {
        params.from_date = from_date;
      }

      if (to_date) {
        params.to_date = to_date;
      }

      // Add search query if provided
      if (search) {
        params.search = search;
      }

      

      const response = await api.get(API_ENDPOINTS.ALERTS, {
        params,
        headers: {
          Accept: "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching alerts:", error);
      throw error;
    }
  },

  getThreatsCount: async () => {
    try {
      const response = await api.get(API_ENDPOINTS.THREATS_COUNT, {
        headers: { Accept: "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching threats count:", error);
      throw error;
    }
  },
  search: async (search: string, page: number = 1, pageSize: number = 10) => {
    try {
      const response = await api.get(API_ENDPOINTS.SEARCH, {
        params: { search, page, page_size: pageSize },
        headers: { Accept: "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error("Error performing search:", error);
      throw error;
    }
  },
  startVSScan: async (payload: any, signal?: AbortSignal) => {
    try {
      const response = await api.post(
        `${BASE_URL_VS}${API_ENDPOINTS.VS_SCAN_NEW}`,
        payload,
        {
          signal, // optional abort support
        }
      );
      return response?.data;
    } catch (error) {
      console.error("Error starting VS scan:", error);
      throw error;
    }
  },
  startVsAuthScan: async (payload: any, signal?: AbortSignal) => {
    try {
      const response = await api.post(
        `${BASE_URL_VS}${API_ENDPOINTS.VS_AUTH_SCAN}`,
        payload,
        {
          signal, // optional abort support
        }
      );
      return response?.data;
    } catch (error) {
      console.error("Error starting VS scan with OTP:", error);
      throw error;
    }
  },
  startVsUnAuthScan: async (payload: any, signal?: AbortSignal) => {
    try {
      const response = await api.post(
        `${BASE_URL_VS}${API_ENDPOINTS.VS_UNAUTH_SCAN}`,
        payload,
        {
          signal, // optional abort support
        }
      );
      return response?.data;
    } catch (error) {
      console.error("Error starting VS scan unauthenticated:", error);
      throw error;
    }
  },
  startVsCombinedUnAuthScan: async (payload: any, signal?: AbortSignal) => {
    try {
      const response = await api.post(
        `${BASE_URL_VS}${API_ENDPOINTS.VS_COMBINED_UNAUTH_SCAN}`,
        payload,
        {
          signal,
        }
      );
      return response?.data;
    } catch (error) {
      console.error("Error starting combined scan unauthenticated:", error);
      throw error;
    }
  },
  startVsCombinedAuthScan: async (payload: any, signal?: AbortSignal) => {
    try {
      const response = await api.post(
        `${BASE_URL_VS}${API_ENDPOINTS.VS_COMBINED_AUTH_SCAN}`,
        payload,
        {
          signal,
        }
      );
      return response?.data;
    } catch (error) {
      console.error("Error starting combined scan with auth:", error);
      throw error;
    }
  },
  startVsNetworkBatchScan: async (payload: any, signal?: AbortSignal) => {
    try {
      const response = await api.post(
        `${BASE_URL_VS}${API_ENDPOINTS.VS_NETWORK_BATCH_SCAN}`,
        payload,
        {
          signal,
        }
      );
      return response?.data;
    } catch (error) {
      console.error("Error starting network batch scan:", error);
      throw error;
    }
  },
  copilotChat: async (payload: any) => {
    try {
      const response = await api.post(
        `${BASE_URL_AI}${API_ENDPOINTS.VS_COPILOT_CHAT}`,
        payload
      );
      return response.data;
    } catch (error) {
      try {
        const fallback = await api.post(
          `${BASE_URL_AI}${API_ENDPOINTS.VS_COPILOT_CHAT_ALIAS}`,
          payload
        );
        return fallback.data;
      } catch (fallbackError) {
        console.error("Error sending copilot chat:", fallbackError);
        throw fallbackError;
      }
    }
  },
  streamCopilotChat: async (
    payload: any,
    handlers: {
      onToken: (chunk: string) => void;
      onDone: (finalPayload: any) => void;
      onError?: (message: string) => void;
    }
  ) => {
    const token = sessionManager.getToken();
    const response = await fetch(
      `${BASE_URL_AI}${API_ENDPOINTS.VS_COPILOT_CHAT_STREAM}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok || !response.body) {
      const msg = `Copilot stream failed (${response.status})`;
      handlers.onError?.(msg);
      throw new Error(msg);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let currentEvent = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line) {
          currentEvent = "";
          continue;
        }
        if (line.startsWith("event:")) {
          currentEvent = line.replace("event:", "").trim();
          continue;
        }
        if (line.startsWith("data:")) {
          const rawData = line.replace("data:", "").trim();
          if (currentEvent === "token") {
            // token events are plain text chunks; keep natural newlines in UI.
            handlers.onToken(rawData + "\n");
            continue;
          }
          if (currentEvent === "done") {
            try {
              const parsed = JSON.parse(rawData);
              handlers.onDone(parsed);
            } catch {
              handlers.onDone({ answer: "", conversation_id: null });
            }
          }
        }
      }
    }
  },
  getCopilotConversationForScan: async (scanId: string) => {
    try {
      const response = await api.get(
        `${BASE_URL_AI}${API_ENDPOINTS.VS_COPILOT_SCAN_CONVERSATION}/${scanId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching copilot conversation:", error);
      throw error;
    }
  },
  generateAttackPaths: async (payload: {
    scan_id: string;
    path_count?: number;
    har_s3_url?: string;
    domain_hints?: string[];
    force_refresh?: boolean;
  }) => {
    try {
      const response = await api.post(
        `${BASE_URL_AI}${API_ENDPOINTS.VS_ATTACK_PATHS_GENERATE}`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error generating attack paths:", error);
      throw error;
    }
  },
  getLatestAttackPaths: async (scanId: string) => {
    try {
      const response = await api.get(
        `${BASE_URL_AI}${API_ENDPOINTS.VS_ATTACK_PATHS_LATEST}/${scanId}/latest`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching latest attack paths:", error);
      throw error;
    }
  },
  generateReport: async (payload: {
    scan_id: string;
    format?: ("pdf" | "docx")[];
    compliance_tags?: string[];
    force_refresh?: boolean;
  }) => {
    try {
      const response = await api.post(
        `${BASE_URL_AI}${API_ENDPOINTS.VS_REPORTS_GENERATE}`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error generating report:", error);
      throw error;
    }
  },
  getLatestReport: async (scanId: string) => {
    try {
      const response = await api.get(
        `${BASE_URL_AI}${API_ENDPOINTS.VS_REPORTS_LATEST}/${scanId}/latest`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching latest report:", error);
      throw error;
    }
  },
  getReportJobStatus: async (jobId: string) => {
    try {
      const response = await api.get(
        `${BASE_URL_AI}${API_ENDPOINTS.VS_REPORTS_JOB_STATUS}/${jobId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching report job status:", error);
      throw error;
    }
  },
  getUserRoles: async () => {
    try {
      const response = await api.get(API_ENDPOINTS.GET_USER_TYPES);
      return response.data;
    } catch (error) {
      console.error("Error fetching user roles:", error);
      throw error;
    }
  },
};

// Utility function to get the stored API key for Code Guard
export const getCodeGuardApiKey = (): string | null => {

  return sessionManager.getApiKey();
};

// Event system for API key updates
const apiKeyUpdateListeners: (() => void)[] = [];

export const onApiKeyUpdate = (callback: () => void) => {
  apiKeyUpdateListeners.push(callback);
  return () => {
    const index = apiKeyUpdateListeners.indexOf(callback);
    if (index > -1) {
      apiKeyUpdateListeners.splice(index, 1);
    }
  };
};

export const userService = {
  getAllUsers: async (
    skip: number = 0,
    limit: number = 10,
    q?: string,
    status?: string,
    user_type?: string,
    signal?: AbortSignal
  ) => {
    try {
      const tenantId = sessionManager.getTenantId();
      
      // Build query params
      const params: any = { skip, limit };
      
      if (tenantId) {
        params.tenant_id = tenantId;
      }
      
      // Add optional filters only if they have values
      if (q) {
        params.q = q;
      }
      
      if (status) {
        params.status = status;
      }
      
      if (user_type) {
        params.user_type = user_type;
      }

      const response = await api.get(
        `${BASE_URL}${API_ENDPOINTS.GET_ALL_USERS}`,
        {
          params,
          headers: { Accept: "application/json" },
          signal,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching users:", error);
      throw error;
    }
  },
  CreateUser: async (userData: any, signal?: AbortSignal) => {
    try {
      const response = await api.post(
        `${BASE_URL}${API_ENDPOINTS.CREATE_USER}`,
        userData,
        {
          headers: { "Content-Type": "application/json" },
          signal,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating user:", error);
      throw error;
    }
  },
  GetUserRoles: async (signal?: AbortSignal) => {
    try {
      const response = await api.get(
        `${BASE_URL}${API_ENDPOINTS.GET_USER_ROLES}`,
        {
          headers: { Accept: "application/json" },
          signal,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching user roles:", error);
      throw error;
    }
  },
  GetUserFeatureAccess: async (signal?: AbortSignal) => {
    try {
      const response = await api.get(
        `${BASE_URL}${API_ENDPOINTS.GET_MODULES_LIST}`,
        {
          headers: { Accept: "application/json" },
          signal,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching user feature access:", error);
      throw error;
    }
  },
  UpdateUser: async (userId: string, userData: any, signal?: AbortSignal) => {
    try {
      const response = await api.put(
        `${BASE_URL}${API_ENDPOINTS.UPDATE_USER}/${userId}`,
        userData,
        {
          headers: { "Content-Type": "application/json" },
          signal,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    }
  },
  UpdateUserStatus: async (userId: string, statusData: any, signal?: AbortSignal) => {
    try {
      const fullUrl = `${BASE_URL}${API_ENDPOINTS.UPDATE_USER_STATUS}/${userId}/`; 
      
      const response = await api.put(fullUrl, statusData, {
        headers: { "Content-Type": "application/json" },
        signal,
      });
      return response.data;
    } catch (error) {
      console.error("Error updating user status:", error);
      throw error;
    }
  },
};

const notifyApiKeyUpdate = () => {
  
  apiKeyUpdateListeners.forEach((callback) => callback());
};

// Debug function for testing API key functionality
// Add test functions to window for debugging
if (typeof window !== "undefined") {
  (window as any).testApiKey = {
    // Test storing and retrieving API key
    test: () => {
      

      // Test storing
      const testKey =
        "$2b$12$dm2bW.Y61x60kjibgFmwe.nrm5wlYqdcdaZVaHczE.iGPgctZCH1G";
      sessionManager.setApiKey(testKey);
      

      // Trigger update notification
      notifyApiKeyUpdate();
      

      return getCodeGuardApiKey();
    },

    // Simulate the exact login response you received
    simulateLogin: () => {
      
      const loginResponse = {
        message: "Login successful",
        access_token: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
        refresh_token: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
        user_id: "68da5eb54d9beb2a2aa2e972",
        api_key: "$2b$12$dm2bW.Y61x60kjibgFmwe.nrm5wlYqdcdaZVaHczE.iGPgctZCH1G",
      };

      

      // Store user session data
      if (loginResponse.user_id) {
        sessionManager.setUserId(loginResponse.user_id);
      }

      if (loginResponse.access_token) {
        sessionManager.setToken(loginResponse.access_token);
      }

      if (loginResponse.refresh_token) {
        sessionManager.setRefreshToken(loginResponse.refresh_token);
      }

      // Store API key
      if (loginResponse.api_key) {
        
        sessionManager.setApiKey(loginResponse.api_key);
        notifyApiKeyUpdate();
      }

      
      return getCodeGuardApiKey();
    },

    // Clear session
    clear: () => {
      sessionManager.clearSession();
      console.log("Session cleared");
    },

    // Check current status
    status: () => {


      return {
        apiKey: getCodeGuardApiKey(),
        sessionApiKey: sessionManager.getApiKey(),
        rawApiKey: sessionManager.getApiKey(),
        userId: sessionManager.getUserId(),
        isAuthenticated: sessionManager.isAuthenticated(),
        listenerCount: apiKeyUpdateListeners.length,
      };
    },
  };
}

export default api;
